<template>
	<div class="table-wrapper">
		<table>
			<thead>
				<tr>
					<th
						v-for="header in headers"
						:key="header.key"
						:class="isActiveSort(header.key)"
						@click="sortBy(header.key)">
						<span class="header-label">
							{{ header.label }}
							<span v-if="header.sortable">
								{{ getSortArrow(header.key) }}
							</span>
						</span>
					</th>
				</tr>
			</thead>
			<tbody>
				<tr v-if="isLoading" class="skeleton-row">
					<td colspan="3"></td>
				</tr>
				<tr v-else-if="sortedAlerts.length == 0" class="no-data-row">
					<td colspan="3" class="no-data">No alerts found</td>
				</tr>
				<tr v-else v-for="alert in sortedAlerts" :key="alert.alertTypeId" class="data-row">
					<td>{{ alert.alertName }}</td>
					<td>
						<span :class="alert.borrowerAlert.statusCssClass">{{ alert.borrowerAlert.statusName }}</span>
					</td>
					<td>
						<span :class="alert.coAlert.statusCssClass">{{ alert.coAlert.statusName }}</span>
					</td>
				</tr>
			</tbody>
		</table>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, onMounted } from 'vue';
	import { useRoute, useRouter } from 'vue-router';
	import { AlertModel } from '@/models/loans';
	import { CreditPullTypes } from '@/types/annual-review';

	const props = defineProps<{
		loanId: number;
		isLoading: boolean;
		alerts: AlertModel[] | undefined;
		borrowerCreditPullTypeId: number;
		coBorrowerCreditPullTypeId: number;
	}>();

	const route = useRoute();
	const router = useRouter();

	const isLoading = computed<boolean>(() => props.isLoading);
	const alerts = computed<AlertModel[]>(() => props.alerts ?? []);

	const sortKey = ref('');
	const sortOrder = ref(1);

	const headers = computed(() => {
		const borrowerLabel =
			props.borrowerCreditPullTypeId === CreditPullTypes.ORIGINAL ? 'Borrower AR Status' : 'Borrower CR Status';

		const coBorrowerLabel =
			props.coBorrowerCreditPullTypeId === CreditPullTypes.ORIGINAL
				? 'Co-Borrower AR Status'
				: 'Co-Borrower CR Status';

		return [
			{ label: 'Alert', sortable: true, key: 'alertTypeId' },
			{ label: borrowerLabel, sortable: true, key: 'borrowerAlert.statusName' },
			{ label: coBorrowerLabel, sortable: true, key: 'coAlert.statusName' }
		];
	});

	const sortedAlerts = computed(() => {
		if (!sortKey.value) {
			return alerts.value; // no sorting, return original order
		}

		return [...alerts.value].sort((a, b) => {
			let valA = getNestedValue(a, sortKey.value);
			let valB = getNestedValue(b, sortKey.value);

			if (typeof valA === 'string') {
				valA = valA.toLowerCase();
			}

			if (typeof valB === 'string') {
				valB = valB.toLowerCase();
			}

			if (valA < valB) {
				return -1 * sortOrder.value;
			} else if (valA > valB) {
				return 1 * sortOrder.value;
			}
			return 0;
		});
	});

	const getNestedValue = (obj: any, path: string): any => {
		return path.split('.').reduce((acc, key) => acc?.[key], obj);
	};

	const sortBy = (key: string) => {
		if (sortKey.value === key) {
			if (sortOrder.value === 1) {
				sortOrder.value = -1; // second click, descending
			} else if (sortOrder.value === -1) {
				sortKey.value = ''; // third click, reset sort
				sortOrder.value = 1; // default sort order for the next click
			}
		} else {
			sortKey.value = key;
			sortOrder.value = 1; // first click, ascending
		}

		router.push({
			query: {
				...route.query,
				alertsSortKey: sortKey.value,
				alertsSortOrder: sortOrder.value.toString()
			},
			hash: route.hash
		});
	};

	const getSortArrow = (key: string) => {
		if (sortKey.value === key) {
			return sortOrder.value === 1 ? '▲' : sortOrder.value === -1 ? '▼' : ' ';
		}

		return '';
	};

	const isActiveSort = (key: string) => (sortKey.value === key ? 'active-sort' : '');

	function intializeSorting() {
		const querySortKey = route.query.alertsSortKey as string;
		const querySortOrder = parseInt(route.query.alertsSortOrder as string, 10);

		if (querySortKey) {
			sortKey.value = querySortKey;
		}

		if (!isNaN(querySortOrder) && (querySortOrder === 1 || querySortOrder === -1)) {
			sortOrder.value = querySortOrder;
		}
	}

	onMounted(async () => {
		intializeSorting();
	});
</script>

<style scoped src="@/assets/css/table.css" />

<style scoped>
	.spinner-container {
		display: flex;
		justify-content: center;
	}
</style>
